import {
    ChangeDetectionStrategy,
    Component,
    type Signal,
    type WritableSignal,
    computed,
    signal,
    inject,
    type OnInit,
    DestroyRef,
    Injector,
    forwardRef,
} from '@angular/core';
import {
    ReactiveFormsModule,
    type ControlValueAccessor,
    type Validator,
    type ValidationErrors,
    type AbstractControl,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    NgControl,
    FormControl,
    type FormControlStatus,
    FormGroup,
} from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, filter, merge, startWith, tap } from 'rxjs';
import { type ErrorStateMatcher } from '@angular/material/core';
import { type ICountryPhoneMetaDto, type IPhoneMetaDto } from '@pecan/api';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import type { IPhoneMaskConfig } from '@pecan/shared/models/phone-mask-config';
import { InvalidLabelComponent } from '@pecan/shared/components/invalid-label/invalid-label.component';
import { PHONE_MASKS, PHONE_CODES } from '@pecan/shared/constants/phone-masks.constant';
import { PHONE_NUMBER_SPECIFICATIONS } from '@pecan/shared/constants/phone-number-specifications';
import { Store } from '@ngxs/store';
import { LocationState } from '../../states/location.state';
import type { IPhoneInfoFormValue } from '../../models/phone-info-form-value.interface';
import { PhoneCodeSelectComponent } from '../phone-code-select/phone-code-select.component';

interface InternationalPhoneFormGroup {
    country: FormControl<ICountryPhoneMetaDto | null>;
    phone: FormControl<string>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        InvalidLabelComponent,
        MatFormField,
        MatInput,
        MatError,
        NgxMaskDirective,
        TranslatePipe,
        PhoneCodeSelectComponent,
    ],
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InternationalPhoneInputComponent),
        },
        {
            multi: true,
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InternationalPhoneInputComponent),
        },
    ],
    selector: 'app-international-phone-input',
    styleUrls: ['./international-phone-input.component.scss'],
    templateUrl: './international-phone-input.component.html',
})
export class InternationalPhoneInputComponent implements ControlValueAccessor, Validator, OnInit {
    public readonly countries: Signal<ICountryPhoneMetaDto[] | null>;

    public readonly phoneMaskConfig: Signal<IPhoneMaskConfig> = computed<IPhoneMaskConfig>(() => {
        const { countryCode } = this._locale();

        if (countryCode === PHONE_CODES.US_CANADA) {
            return {
                mask: PHONE_MASKS.US_CANADA,
                maxLength: PHONE_NUMBER_SPECIFICATIONS.US_CANADA.MASK_LENGTH,
            };
        }

        return {
            mask: PHONE_MASKS.INTERNATIONAL,
            maxLength: PHONE_NUMBER_SPECIFICATIONS.INTERNATIONAL.MAX_LENGTH - countryCode.length,
        };
    });

    public readonly form: FormGroup<InternationalPhoneFormGroup> = new FormGroup<InternationalPhoneFormGroup>({
        country: new FormControl<ICountryPhoneMetaDto | null>(null),
        phone: new FormControl<string>('', { nonNullable: true }),
    });

    public readonly hasPhoneError: Signal<boolean> = computed<boolean>(
        () => !this._disabled() && (this._phoneStatus() === 'INVALID' || this._hostControlInvalid())
    );

    public readonly phoneErrorStateMatcher: ErrorStateMatcher = {
        isErrorState: (): boolean => this.hasPhoneError(),
    };

    private readonly _locale: WritableSignal<IPhoneMetaDto> = signal<IPhoneMetaDto>({ isoCode: '', countryCode: '' });

    private readonly _store: Store = inject(Store);

    private readonly _injector: Injector = inject(Injector);

    private readonly _destroyRef: DestroyRef = inject(DestroyRef);

    private readonly _phoneStatus: Signal<FormControlStatus> = toSignal(this.form.controls.phone.statusChanges, {
        initialValue: this.form.controls.phone.status,
    });

    private readonly _hostControlInvalid: WritableSignal<boolean> = signal<boolean>(false);

    private readonly _disabled: WritableSignal<boolean> = signal<boolean>(false);

    private _ngControl: NgControl | null = null;

    constructor() {
        this.countries = this._store.selectSignal(LocationState.countries);
    }

    public ngOnInit(): void {
        this._ngControl = this._injector.get(NgControl, null);

        this._listenCountryChanges();
        this._listenPhoneStatusChanges();
        this._listenHostControlValidity();
    }

    public validate(_control: AbstractControl): ValidationErrors | null {
        return this.form.controls.phone.valid ? null : { invalidPhone: true };
    }

    public writeValue(value: IPhoneInfoFormValue | null): void {
        if (!value) {
            return;
        }

        const locale: IPhoneMetaDto = { isoCode: value.locale?.isoCode ?? '', countryCode: value.locale?.countryCode ?? '' };

        this._locale.set(locale);

        this.form.setValue({ country: locale as ICountryPhoneMetaDto, phone: value.phone ?? '' }, { emitEvent: false });
        this.form.controls.phone.updateValueAndValidity({ onlySelf: true });
    }

    public registerOnChange(fn: (value: IPhoneInfoFormValue) => void): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this._disabled.set(isDisabled);

        if (isDisabled) {
            this.form.disable({ emitEvent: false });
        } else {
            this.form.enable({ emitEvent: false });
        }
    }

    public onPhoneInput(): void {
        this._notifyValueChange();
        this._onTouched();
    }

    private _listenCountryChanges(): void {
        this.form.controls.country.valueChanges
            .pipe(
                filter((country: ICountryPhoneMetaDto | null): country is ICountryPhoneMetaDto => country !== null),
                takeUntilDestroyed(this._destroyRef),
                tap((country: ICountryPhoneMetaDto) => {
                    this._locale.set({ isoCode: country.isoCode, countryCode: country.countryCode });
                    this.form.controls.phone.setValue('');
                })
            )
            .subscribe(() => {
                this._notifyValueChange();
                this._onTouched();
            });
    }

    private _listenPhoneStatusChanges(): void {
        this.form.controls.phone.statusChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._ngControl?.control?.updateValueAndValidity({ emitEvent: false }));
    }

    private _listenHostControlValidity(): void {
        const host = this._ngControl?.control;

        if (!host) {
            return;
        }

        merge(host.events, host.parent?.events ?? EMPTY)
            .pipe(startWith(null), takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._hostControlInvalid.set(this._isHostControlInvalid(host)));
    }

    private _isHostControlInvalid(host: AbstractControl): boolean {
        return host.touched && (host.invalid || Boolean(host.parent?.errors?.phoneRequired));
    }

    private _notifyValueChange(): void {
        this._onChange({ phone: this.form.controls.phone.value, locale: this._locale() });
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onChange: (value: IPhoneInfoFormValue) => void = () => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onTouched: () => void = () => {};
}
